import { cx } from 'emotion';
import { isEmpty, applySpec } from 'ramda';
import {
  useMemo,
  lazy,
  Suspense,
  useRef,
  useEffect,
  useState,
  memo,
} from 'react';

import { selectors } from '@ezugi/bootstrap';
import BoardToggle from '@ezugi/bootstrap/src/components/BoardToggle';
import ProgressBar from '@ezugi/bootstrap/src/components/ProgressBar';
import { useAppState } from '@ezugi/bootstrap/src/hooks/useAppState';
import { useGameProgress } from '@ezugi/bootstrap/src/hooks/useGameProgress';
import { useOrientationChange } from '@ezugi/hooks/src/useOrientationChange';
import { DeviceInfo } from '@ezugi/utils';

import LastRounds from '../../components/LastRounds';
import { BetsContextProvider } from '../../contexts/bets';
import { dynamicMultipliersSelector, isSicboMultiplierSelector } from '../../store/selectors/game';
import { gameVideoHandler } from '../../utils';
import { mobileGameUIClassName } from './GameBoard.css';
import { GRID_SCALE_DELAY } from './constants';
import Left from './sections/Left';
import {
  Section,
  Container,
  centerSectionClassName,
  mobileCenterSectionClassName,
  desktopBetGridClassName,
  inactiveGrid,
  progressBarClassName,
  mobileContainerClassName,
  mobileBetsClosedClassName,
  gridUpClassName,
  downscaledSideSections,
  desktopContainer,
  mobileLastRounds,
  lastRoundsNMB,
  rightSectionClassName,
  sicboStudioColor,
  expandClassName,
} from './style';
import { useFlyingChips } from './useFlyingChips';

const DesktopBetGrid = lazy(() => import('../../components/BetGrid/desktop'));
const LandscapeBetGrid = lazy(() => import('../../components/BetGrid/landscape'));
const PortraitBetGrid = lazy(() => import('../../components/BetGrid/portrait'));
const Statistics = lazy(() => import('../Statistics'));

const { videoReadyStatusSelector, boardToggledSelector } = selectors;

const spec = applySpec({
  videoReady: videoReadyStatusSelector,
  multipliers: dynamicMultipliersSelector,
  isSicboMultiplier: isSicboMultiplierSelector,
  toggled: boardToggledSelector,
});

function GameBoard() {
  const [ { videoReady, multipliers, isSicboMultiplier, toggled } ] = useAppState(spec);
  const orientation = useOrientationChange();
  const progress = useGameProgress();
  const scrollRef = useRef();
  const [ gridUp, setGridUp ] = useState(false);
  const gridUpRef = useRef();

  const isLandscape = orientation === 'landscape';

  const isMobile = DeviceInfo.isHandheld;

  const isMobileLandscape = isMobile && isLandscape;

  const gameVideo = document.querySelector('[data-e2e="game-video"]');

  /**
   * When gameVideo updates we recalculate the blackbars width
   * so we can reposition the gradients correctly
   */
  useEffect(() => {
    if (!videoReady || !DeviceInfo.isHandheld) return;

    gameVideo?.addEventListener('transitionend', gameVideoHandler);

    // eslint-disable-next-line
    return () => gameVideo?.removeEventListener('transitionend', gameVideoHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ videoReady ]);

  const BetGrid = useMemo(() => {
    if (DeviceInfo.isHandheld) {
      if (orientation === 'portrait') return PortraitBetGrid;

      return LandscapeBetGrid;
    }

    return DesktopBetGrid;
  }, [ orientation ]);

  useEffect(() => {
    if (DeviceInfo.isHandheld) {
      document.body.classList.add(sicboStudioColor);
    }
  }, []);

  const [ hasDelay, setHasDelay ] = useState(false);

  useEffect(() => {
    isMobile && !isEmpty(multipliers) && setTimeout(() => {
      setHasDelay(false);
    }, 2500);
  }, [ multipliers, isMobile ]);

  useEffect(() => {
    isMobile && isSicboMultiplier && setHasDelay(!progress.areBetsOpen);
  }, [ progress.areBetsOpen, isSicboMultiplier, isMobile ]);

  // Put grid down when bets are closed
  useEffect(() => {
    if (!progress.areBetsOpen) {
      setGridUp(false);
    }
  }, [ progress.areBetsOpen ]);

  useFlyingChips();

  return (
    <BoardToggle
      className={DeviceInfo.isHandheld ? mobileGameUIClassName : undefined}
      enableToggle={true}
    >
      <Container
        className={cx(
          DeviceInfo.isHandheld ? mobileContainerClassName : desktopContainer,
          {
            [mobileBetsClosedClassName]: DeviceInfo.isHandheld && !progress.areBetsOpen && !hasDelay,
            [expandClassName]: isMobileLandscape && hasDelay,
          }
        )}
        data-name="game-board"
      >
        {!DeviceInfo.isHandheld && (
          <Left className={cx({ [downscaledSideSections]: gridUp })} />
        )}
        <Section
          data-name="center-section"
          className={cx(centerSectionClassName, {
            [mobileCenterSectionClassName]: DeviceInfo.isHandheld,
            [gridUpClassName]: gridUp && !DeviceInfo.isHandheld && progress.areBetsOpen,
          })}
          onMouseEnter={() => {
            clearTimeout(gridUpRef.current);
            if (progress.areBetsOpen && !toggled) {
              setGridUp(true);
            }
          }}
          onMouseLeave={() => {
            clearTimeout(gridUpRef.current);
            gridUpRef.current = setTimeout(() => {
              setGridUp(false);
            }, GRID_SCALE_DELAY);
          }}
          ref={scrollRef}
        >
          {!DeviceInfo.isHandheld && <ProgressBar className={progressBarClassName} />}

          <Suspense fallback={null}>
            <BetsContextProvider>
              <BetGrid
                className={cx({
                  [desktopBetGridClassName]: !DeviceInfo.isHandheld,
                  [inactiveGrid]: !progress.areBetsOpen,
                })}
              />
            </BetsContextProvider>
          </Suspense>
        </Section>
        {!DeviceInfo.isHandheld && (
          <Section
            data-name="right-section"
            className={cx(rightSectionClassName, {
              [downscaledSideSections]: gridUp,
            })}
          >
            <Suspense fallback={null}>
              <Statistics />
            </Suspense>
          </Section>
        )}
      </Container>
      {DeviceInfo.isHandheld && (
        <LastRounds
          constrain={true}
          showDices={true}
          flatView={true}
          className={cx(mobileLastRounds, {
            [lastRoundsNMB]: !progress.areBetsOpen,
          })}
        />
      )}
    </BoardToggle>
  );
}

export default memo(GameBoard);
